import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Download, Link2, X, ChevronRight, Sparkles } from 'lucide-react';
import { trackEvent, EVENTS } from '../utils/posthog';
import { triggerSubtleConfetti } from '../utils/confetti';
interface SpotlightStep {
  id: string;
  target: string; // CSS selector
  title: string;
  description: string;
  action: string;
  icon: ComponentType<{
    className?: string;
  }>;
  onAction: () => void;
  position?: 'top' | 'bottom' | 'left' | 'right';
}
interface SpotlightTourProps {
  steps: SpotlightStep[];
  onComplete: () => void;
  onDismiss: () => void;
}
export function SpotlightTour({
  steps,
  onComplete,
  onDismiss
}: SpotlightTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const step = steps[currentStep];
  // Update target position
  useEffect(() => {
    const updateTargetPosition = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
      }
    };
    updateTargetPosition();
    setIsVisible(true);
    // Track step view
    trackEvent('spotlight_step_viewed', {
      step_number: currentStep + 1,
      step_id: step.id,
      total_steps: steps.length
    });
    // Observe target element for position changes
    const element = document.querySelector(step.target);
    if (element) {
      observerRef.current = new ResizeObserver(updateTargetPosition);
      observerRef.current.observe(element);
    }
    // Update on scroll and resize
    window.addEventListener('scroll', updateTargetPosition);
    window.addEventListener('resize', updateTargetPosition);
    return () => {
      window.removeEventListener('scroll', updateTargetPosition);
      window.removeEventListener('resize', updateTargetPosition);
      observerRef.current?.disconnect();
    };
  }, [currentStep, step]);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 300);
    } else {
      handleComplete();
    }
  };
  const handleAction = () => {
    triggerSubtleConfetti(document.querySelector(step.target) as HTMLElement);
    trackEvent('spotlight_action_clicked', {
      step_id: step.id,
      action: step.action
    });
    step.onAction();
    handleNext();
  };
  const handleDismiss = () => {
    trackEvent('spotlight_dismissed', {
      step_number: currentStep + 1,
      step_id: step.id
    });
    setIsVisible(false);
    setTimeout(() => {
      onDismiss();
    }, 300);
  };
  const handleComplete = () => {
    trackEvent('spotlight_completed', {
      total_steps: steps.length
    });
    setIsVisible(false);
    setTimeout(() => {
      onComplete();
    }, 300);
  };
  if (!targetRect) return null;
  // Calculate tooltip position
  const getTooltipPosition = () => {
    const position = step.position || 'bottom';
    const spacing = 20;
    const tooltipWidth = 320;
    switch (position) {
      case 'bottom':
        return {
          top: targetRect.bottom + spacing,
          left: targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        };
      case 'top':
        return {
          top: targetRect.top - spacing - 200,
          left: targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        };
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2 - 100,
          left: targetRect.left - tooltipWidth - spacing
        };
      case 'right':
        return {
          top: targetRect.top + targetRect.height / 2 - 100,
          left: targetRect.right + spacing
        };
      default:
        return {
          top: targetRect.bottom + spacing,
          left: targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        };
    }
  };
  const tooltipPosition = getTooltipPosition();
  return <AnimatePresence data-id="element-787">
      {isVisible && <>
          {/* Dimmed Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]" onClick={handleDismiss} data-id="element-788" />

          {/* Spotlight Ring */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} transition={{
        duration: 0.4,
        type: 'spring',
        stiffness: 300
      }} className="fixed z-[9999] pointer-events-none" style={{
        top: targetRect.top - 8,
        left: targetRect.left - 8,
        width: targetRect.width + 16,
        height: targetRect.height + 16
      }} data-id="element-789">
            {/* Animated pulse ring */}
            <motion.div animate={{
          scale: [1, 1.05, 1],
          opacity: [0.8, 1, 0.8]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-lg border-2 border-sage-mint shadow-glow-mint" data-id="element-790" />
            {/* Static ring */}
            <div className="absolute inset-0 rounded-lg border-2 border-sage-mint/50" data-id="element-791" />
          </motion.div>

          {/* Tooltip Card */}
          <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="fixed z-[10000] w-80" style={{
        top: tooltipPosition.top,
        left: Math.max(16, Math.min(tooltipPosition.left, window.innerWidth - 320 - 16))
      }} data-id="element-792">
            <div className="bg-white rounded-xl shadow-lg border border-sage-mint/20 overflow-hidden" data-id="element-793">
              {/* Header */}
              <div className="bg-gradient-to-r from-sage-mint/10 to-coral-pink/10 p-4 border-b border-grey-400/10" data-id="element-794">
                <div className="flex items-start justify-between" data-id="element-795">
                  <div className="flex items-start space-x-3" data-id="element-796">
                    <div className="w-10 h-10 rounded-full bg-sage-mint/20 flex items-center justify-center flex-shrink-0" data-id="element-797">
                      <step.icon className="h-5 w-5 text-sage-mint" data-id="element-798" />
                    </div>
                    <div data-id="element-799">
                      <h3 className="text-base font-medium text-charcoal" data-id="element-800">
                        {step.title}
                      </h3>
                      <p className="text-xs text-grey-600 mt-1" data-id="element-801">
                        Step {currentStep + 1} of {steps.length}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleDismiss} className="text-grey-400 hover:text-grey-600 transition-colors" data-id="element-802">
                    <X className="h-4 w-4" data-id="element-803" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-4" data-id="element-804">
                <p className="text-sm text-grey-600 leading-relaxed" data-id="element-805">
                  {step.description}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4" data-id="element-806">
                  <button onClick={handleAction} className="flex-1 bg-sage-mint text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sage-mint/90 transition-all duration-300 hover:shadow-glow-mint flex items-center justify-center group" data-id="element-807">
                    <span data-id="element-808">{step.action}</span>
                    <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" data-id="element-809" />
                  </button>
                  <button onClick={handleNext} className="px-4 py-2.5 text-sm font-medium text-grey-600 hover:text-charcoal transition-colors" data-id="element-810">
                    Skip
                  </button>
                </div>

                {/* Progress Dots */}
                <div className="flex items-center justify-center gap-1.5 mt-4" data-id="element-811">
                  {steps.map((_, index) => <motion.div key={index} initial={false} animate={{
                width: index === currentStep ? 24 : 6,
                backgroundColor: index === currentStep ? 'var(--sage-mint)' : index < currentStep ? 'var(--sage-mint)' : 'rgb(209, 213, 219)'
              }} transition={{
                duration: 0.3
              }} className="h-1.5 rounded-full" data-id="element-812" />)}
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export default SpotlightTour;